/**
 * Session 60.21 · SaveSpaceButton
 *
 * Small heart/bookmark toggle that turns any space card into a saveable
 * one. Two visual variants: `pill` (default, gold pill) and `icon-only`
 * (compact for tight cards).
 */
import React from "react";
import { useIsSaved } from "../lib/savedSpaces";

export default function SaveSpaceButton({
  spaceType,          // "cafe" | "workspace"
  spaceId,
  spaceName,
  variant = "pill",
  className = "",
}) {
  const [saved, toggle, busy] = useIsSaved(spaceType, spaceId);
  const label = saved ? "Saved" : "Save";

  const onClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggle();
  };

  if (variant === "icon-only") {
    return (
      <button
        type="button"
        onClick={onClick}
        disabled={busy}
        title={saved ? `Saved — click to remove ${spaceName || ""}` : `Save ${spaceName || "this space"}`}
        aria-label={label}
        data-testid={`save-${spaceType}-${spaceId}`}
        className={className}
        style={{
          border: "1px solid " + (saved ? "rgba(184,145,58,0.55)" : "rgba(255,255,255,0.18)"),
          background: saved ? "rgba(184,145,58,0.16)" : "transparent",
          color: saved ? "#B8913A" : "inherit",
          width: 34, height: 34, borderRadius: 999,
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          cursor: busy ? "wait" : "pointer",
        }}
      >
        <Bookmark filled={saved} />
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      data-testid={`save-${spaceType}-${spaceId}`}
      className={className}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        padding: "6px 12px", borderRadius: 999,
        fontSize: 12, fontWeight: 600, letterSpacing: "0.04em",
        border: "1px solid " + (saved ? "rgba(184,145,58,0.55)" : "rgba(27,35,64,0.18)"),
        background: saved ? "rgba(184,145,58,0.14)" : "transparent",
        color: saved ? "#B8913A" : "inherit",
        cursor: busy ? "wait" : "pointer",
        transition: "background 120ms ease, color 120ms ease",
      }}
    >
      <Bookmark filled={saved} />
      <span>{saved ? "Saved" : "Save"}</span>
    </button>
  );
}

function Bookmark({ filled }) {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M6 4a2 2 0 0 0-2 2v14l8-4 8 4V6a2 2 0 0 0-2-2H6z" />
    </svg>
  );
}
